import { 
  collection, 
  addDoc, 
  serverTimestamp, 
  query, 
  orderBy, 
  limit, 
  where,
  doc,
  updateDoc,
  increment,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { Post } from '../types';

/**
 * Create a new post
 */
export const createPost = async (
  userId: string,
  userName: string,
  userAvatar: string,
  userLevel: number | undefined, // authorLevel (embedded for denormalization)
  data: {
    text: string;
    imageUrl?: string;
    videoUrl?: string;
    mediaType?: 'image' | 'video';
    type?: 'workout' | 'meal' | 'achievement' | 'general';
    privacy?: 'public' | 'friends' | 'private';
  }
): Promise<string> => {
  const postsRef = collection(db, "posts");
  
  const postData: Omit<Post, 'id'> = {
    userId, // authorId (embedded)
    userName, // authorName (embedded)
    userAvatar, // authorAvatar (embedded)
    userLevel: userLevel || 1, // authorLevel (embedded, default to 1 if not provided)
    text: data.text.trim(),
    imageUrl: data.imageUrl || undefined,
    videoUrl: data.videoUrl || undefined,
    mediaType: data.mediaType,
    likes: 0,
    commentsCount: 0,
    timestamp: serverTimestamp(),
    type: data.type || 'general',
    privacy: data.privacy || 'public',
    status: 'active'
  };

  const docRef = await addDoc(postsRef, postData);
  return docRef.id;
};

/**
 * Get posts feed (public posts, ordered by timestamp)
 * 🔒 CRITICAL: Uses one-shot getDocs() instead of onSnapshot to avoid permission-denied errors
 * Feed is public data - no realtime needed
 */
export const getPostsFeed = async (limitCount: number = 20): Promise<Post[]> => {
  try {
    const q = query(
      collection(db, "posts"),
      orderBy("timestamp", "desc"),
      limit(limitCount * 2) // Get more to filter client-side
    );

    const snapshot = await getDocs(q);
    
    // Only log in development - production console should be clean
    if (import.meta.env.DEV) {
      console.log('[POSTS] Firestore query received:', snapshot.size, 'docs');
    }
    
    const posts = snapshot.docs
      .map(doc => {
        const data = doc.data();
        // Backward compatibility: convert old 'image' field to 'imageUrl'
        if (data.image && !data.imageUrl) {
          data.imageUrl = data.image;
        }
        return {
          id: doc.id,
          ...data
        } as Post;
      })
      .filter(post => {
        // Client-side filtering for status, privacy, and public field
        // In production, add Firestore composite indexes for better performance
        const status = post.status || 'active';
        const privacy = post.privacy || 'public';
        
        // CRITICAL: Filter by public field - include if public == true OR field is missing
        const isPublic = post.public === true || post.public === undefined || post.public === null;
        
        const isActive = status === 'active' && privacy === 'public' && isPublic;
        if (!isActive && import.meta.env.DEV) {
          // Only log in development - production console should be clean
          console.log('[POSTS] Filtered out post:', post.id, { status, privacy, public: post.public });
        }
        return isActive;
      })
      .slice(0, limitCount); // Limit after filtering
    
    // Only log in development - production console should be clean
    if (import.meta.env.DEV) {
      console.log('[POSTS] Filtered posts:', posts.length);
    }
    
    return posts;
  } catch (error: any) {
    // CRITICAL: Handle errors gracefully - don't crash the app
    if (error?.code === 'permission-denied') {
      if (import.meta.env.DEV) {
        console.warn("[POSTS] Permission denied for posts query - user may not have access");
      }
      return []; // Return empty array - UI shows empty feed instead of crashing
    } else if (error?.code === 'failed-precondition') {
      // If it's an index error, show helpful message
      if (import.meta.env.DEV) {
        console.warn("[POSTS] Firestore index missing. Posts may not load correctly.");
      }
      return [];
    } else {
      if (import.meta.env.DEV) {
        console.error("[POSTS] Error fetching posts:", error);
        console.error("[POSTS] Error details:", error?.code, error?.message);
      }
      return [];
    }
  }
};

/**
 * Get posts by user ID
 * 🔒 CRITICAL: Uses one-shot getDocs() instead of onSnapshot to avoid permission-denied errors
 * Posts of other users - no realtime needed
 */
export const getUserPosts = async (userId: string, limitCount: number = 20): Promise<Post[]> => {
  try {
    // Query with client-side filtering (works without composite index)
    const q = query(
      collection(db, "posts"),
      orderBy("timestamp", "desc"),
      limit(100) // Fetch more to account for filtering
    );

    const snapshot = await getDocs(q);
    
    // Only log in development - production console should be clean
    if (import.meta.env.DEV) {
      console.log('[USER_POSTS] Firestore query received:', snapshot.size, 'docs');
    }
    
    const posts = snapshot.docs
      .map(doc => {
        const data = doc.data();
        // Backward compatibility
        if (data.image && !data.imageUrl) {
          data.imageUrl = data.image;
        }
        return {
          id: doc.id,
          ...data
        } as Post;
      })
      .filter(post => {
        // Filter by userId and status
        const matchesUser = post.userId === userId;
        const status = post.status || 'active';
        return matchesUser && status === 'active';
      })
      .slice(0, limitCount); // Limit after filtering
    
    // Only log in development - production console should be clean
    if (import.meta.env.DEV) {
      console.log('[USER_POSTS] Filtered posts:', posts.length, 'for user', userId);
    }
    
    return posts;
  } catch (error: any) {
    // CRITICAL: Handle errors gracefully - don't crash the app
    if (error?.code === 'permission-denied') {
      if (import.meta.env.DEV) {
        console.warn("[USER_POSTS] Permission denied for user posts query - user may not have access");
      }
      return []; // Return empty array - UI shows empty feed instead of crashing
    } else {
      if (import.meta.env.DEV) {
        console.error("[USER_POSTS] Error fetching user posts:", error);
        console.error("[USER_POSTS] Error details:", error?.code, error?.message);
      }
      return [];
    }
  }
};


/**
 * Like a post
 * NOTE: This is a simple implementation that increments likes.
 * For production, consider tracking likedBy array to prevent double-likes.
 */
export const likePost = async (postId: string, userId?: string): Promise<void> => {
  try {
    const postRef = doc(db, "posts", postId);
    await updateDoc(postRef, {
      likes: increment(1)
    });
  } catch (error: any) {
    console.error('[POST] Error liking post:', error);
    // Re-throw to allow UI to handle the error
    throw new Error(error.message || 'Failed to like post');
  }
};

/**
 * Delete a post
 */
export const deletePost = async (postId: string, userId: string): Promise<void> => {
  // In production, add security check to ensure userId matches post userId
  const postRef = doc(db, "posts", postId);
  await updateDoc(postRef, {
    status: 'hidden' // Soft delete for moderation
  });
};

